import { animate, state, style, transition, trigger } from '@angular/animations';
import {
	AfterViewInit,
	Component, ContentChild,
	ElementRef,
	Input,
	TemplateRef,
	ViewChild
} from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { Observable, Subject, timer } from 'rxjs';
import { IColumnType, IRowType, ITableData, ITableRowClickData } from '../../models/table';
import { TableService } from '../../services/table.service';
import { Translates } from './translates';
import { ListsColumnsKey } from '../../services/session.service';
import { SessionService } from '../../services/session.service';
import { Utils } from '../../services/utils.service';
import { MatMenu } from '@angular/material/menu';
import { SnackBarService } from '../../services/snack-bar.service';
import { SortingListData } from '../ph-list/ph-list.component';
import { MatTableDataSource } from '@angular/material/table';

import { SHARED_MODULES } from '../../../shared.';
import { DtypeInjectorPipe } from '../../pipes/dtype-injector.pipe';
import { DndDraggableDirective } from 'ngx-drag-drop';
import { InfiniteScrollDirective } from 'ngx-infinite-scroll';
import { LoaderComponent } from '../loader/loader.component';
import { PhIconComponent } from '../ph-icon/ph-icon.component';
import { PhIconWrapperComponent } from '../ph-icon-wrapper/ph-icon-wrapper.component';

@Component({
    selector: 'app-ph-simple-table',
    templateUrl: 'ph-simple-table.component.html',
    styleUrls: ['ph-simple-table.component.scss'],
    animations: [
        trigger('detailExpand', [
            state('collapsed', style({ height: '0', minHeight: '0' })),
            state('expanded', style({ height: '*' })),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ])
    ],
	imports: [
		SHARED_MODULES,
		DtypeInjectorPipe,
		DndDraggableDirective,
		InfiniteScrollDirective,
		LoaderComponent,
		PhIconComponent,
		PhIconWrapperComponent
	],
    standalone: true
})
export class PhSimpleTableComponent implements AfterViewInit {

	columns: Array<IColumnType> = [];
	displayColumns: Array<string> = [];
	dtypes: any = null;
	expandedChildren: Array<number> = [];
	infiniteScrollDisabled: boolean = false;
	nextPage: string = null;
	noFilterData: boolean = false;
	noData: boolean = true;
	settingsColumns: Array<IColumnType>;
	showLoader: boolean = false;
	tableData: MatTableDataSource<IRowType>;
	translates = Translates;
	visibleColumnsCount: number = 0;

	@Input() actionButton: boolean = false;
	@Input() actionButtonCount: number = 1;
	@Input() activeGrouping: string = '';
	@Input() columnsKey: ListsColumnsKey;
	@Input() groupingMenu: MatMenu;
	@Input() isColumnsSettings: boolean = false;
	@Input() isExpanded: boolean = false;
	@Input() rowClickSubject: Subject<ITableRowClickData>;
	@Input() savedSort: SortingListData;
	@Input() sortingClearDisabled: boolean = false;
	@Input() sortingSubject: Subject<string>;
	@Input() startDraggingSubject: Subject<any>;
	@Input() doubleClickSubject: Subject<any>;
	@Input() translateColumns: Array<string> = [];
	@Input() updateSubject: Subject<void>;
	@Input() loadExpandedCallback: (id: number) => Observable<any>;
	@Input() loadChildrenCallback: (key: any) => Observable<any>;
	@Input('dataSource') set dataSource(data: ITableData<any>) {
		this.init(data);
	}

	@ViewChild(MatSort) sort!: MatSort;
	@ViewChild('tableContainer', { read: ElementRef }) tableContainerElement: ElementRef;

	@ContentChild(TemplateRef) templateRef: TemplateRef<any>;

	constructor(
		private sessionService: SessionService,
		private tableService: TableService,
		private snackBarService: SnackBarService
	) {
		this.dtypes = this.tableService.dtype;
	}

	ngAfterViewInit(): void {
		this.sort.disableClear = this.sortingClearDisabled;
		this.sort.sortChange.subscribe(() => {
			let sorting;
			if (!this.sort.direction) {
				sorting = 'default';
			} else {
				if (this.sort.direction === 'asc') {
					sorting = this.sort.active;
				} else {
					sorting = '-' + this.sort.active;
				}
			}

			this.sortingSubject.next(sorting);
		});
	}

	init(data: ITableData<any>): void {
		this.showLoader = false;
		if (this.isExpanded) {
			this.expandedChildren = this.expandedChildren.filter((id: number) =>
				data.results.dataSource.map(data => data.key).includes(id)
			);
			data.results.dataSource.forEach(row => row.expanded = this.expandedChildren.includes(row.key));
		}

		if (!this.columns.length) {
			data.results.columns.forEach((column: IColumnType) => {
				if (column.button || column.always_visible || !this.isColumnsSettings) {
					column.visible = true;
					return;
				}
				if (!this.columnsKey || !this.sessionService.defaults.settings[this.columnsKey]) {
					column.visible = column.default_visible;
				} else {
					column.visible = this.sessionService.defaults.settings[this.columnsKey].includes(column.key);
				}
			});
			this.columns = data.results.columns;
			this.settingsColumns = JSON.parse(JSON.stringify(this.columns.filter((column: IColumnType) => !column.button)));
			this.visibleColumnsCount = this.settingsColumns.filter((column: IColumnType) => column.visible).length;
			Utils.calculateColumnsSize(data.results.columns, this.visibleColumnsCount);
			this.displayColumns = this.columns.map((column: IColumnType) => column.key);
			if (this.isExpanded || this.isColumnsSettings || this.groupingMenu || this.actionButton) this.displayColumns = [...this.displayColumns, 'optional'];
		} else {
			data.results.columns.forEach((column: IColumnType) => {
				const findColumn: IColumnType = this.columns.find((c: IColumnType) => c.key === column.key);
				if (findColumn) findColumn.dtype = column.dtype;
			});
		}

		this.tableData = new MatTableDataSource(data.results.dataSource);
		this.nextPage = data.next;
		this.noData = !data.has_data;
		this.noFilterData = data.has_data && !data.results.dataSource.length;
	}

	loadNext(): void {
		if (this.nextPage && this.updateSubject) this.updateSubject.next();
	}

	scrollToTop(): void {
		this.infiniteScrollDisabled = true;
		this.tableContainerElement.nativeElement.scrollTo({ top: 0, behavior: 'smooth' });
		timer(500).subscribe(() => this.infiniteScrollDisabled = false);
	}

	setDisplayingColumns(): void {
		const displayedColumns: Array<string> = this.settingsColumns
			.filter((column: IColumnType) => column.visible)
			.map((column: IColumnType) => column.key);

		if (!Utils.equalLists(this.sessionService.defaults.settings[this.columnsKey] || [], displayedColumns)) {
			this.columns.forEach((column: IColumnType) => {
				column.visible = column.button || column.always_visible || displayedColumns.includes(column.key)
			});

			this.visibleColumnsCount = this.settingsColumns.filter((column: IColumnType) => column.visible).length;
			Utils.calculateColumnsSize(this.columns, this.visibleColumnsCount);

			if (this.columnsKey) {
				this.sessionService.defaults.settings[this.columnsKey] = displayedColumns;
				this.sessionService.updateSettings();
			}
		}
	}

	startDragging(element: any): void {
		if (this.startDraggingSubject) this.startDraggingSubject.next(element);
	}

	startLoading(): void {
		this.showLoader = true;
	}

	toggleExpand(row): void {
		row.expanded = !row.expanded;
		if (row.expanded) {
			this.expandedChildren.push(row.key);
			if (row.expanded_children.length === 0) this.loadExpandedCallback(row.key).subscribe(response => row.expanded_children = response);
		} else {
			this.expandedChildren = this.expandedChildren.filter(child => child !== row.key);
		}
	}

	closeGroup(element): void {
		const index: number = this.tableData.data.indexOf(element),
			data: Array<any> = this.tableData.data;
		if (index === -1) return;

		element.opened = false;
		data.splice(index + 1, element.children.length);
		this.tableData = new MatTableDataSource<IRowType>(data);
	}

	onDoubleClick(element: any): void {
		if (this.doubleClickSubject) this.doubleClickSubject.next(element);
	}

	onRowClick(event: MouseEvent, element: any): void {
		if (this.rowClickSubject) {
			this.rowClickSubject.next({
				event: event,
				element: element
			});
		} else {
			if (element.expanded_children) this.toggleExpand(element);
		}
	}

	openGroup(element): void {
		if (!Object.keys(element.children).length) {
			if (!this.loadChildrenCallback) return;
			element.opened = true;
			this.loadChildrenCallback(element.key).subscribe((response: any) => {
				const index: number = this.tableData.data.indexOf(element),
					data: Array<any> = this.tableData.data;
				if (index === -1) return;

				element.children = response.dataSource;
				element.children.forEach(child => child.level = 2);
				data.splice(index + 1, 0, ...element.children);
				this.tableData = new MatTableDataSource<IRowType>(data);
			});
		} else {
			element.opened = true;
			const index: number = this.tableData.data.indexOf(element),
				data: Array<any> = this.tableData.data;
			if (index === -1) return;

			try {
				data.splice(index + 1, 0, ...element.children);
			} catch (error) {
				element.opened = false;
				this.snackBarService.setErrorNotification();
			}

			this.tableData = new MatTableDataSource<IRowType>(data);
		}
	}
}
